import { ChangeEvent as ReactChangeEvent, FC, useCallback, useMemo, useState } from 'react';
import { PositionID, PositionNames } from '../../../common/Positions/Constants';
import { RemoteProcedureCall, useApplicationStateLoader } from '@resistdesign/voltra/app/utils';
import { APP_ENV_CONFIG } from '../../config';
import { ADMIN } from './IDs';

const {
  api: { domain: API_DOMAIN },
} = APP_ENV_CONFIG;

const COPY_SIGN_UP_URL_LABELS = {
  COPY: 'Copy Sign-Up URL',
  COPIED: 'Copied Sign-Up URL',
};

export type AdminToolsProps = {
  authorization: string;
  groupAdminIdCode?: string;
  adminIsSystemAdmin: boolean;
};

export const AdminTools: FC<AdminToolsProps> = ({ authorization, groupAdminIdCode, adminIsSystemAdmin }) => {
  const SYNC_USER_PROFILES_RPC = useMemo<RemoteProcedureCall>(
    () => ({
      serviceConfig: {
        protocol: 'https',
        domain: API_DOMAIN,
        basePath: '/admin',
        authorization,
      },
      path: '/sync-user-profiles',
      args: [],
    }),
    [authorization]
  );
  const { loading: syncUserProfilesLoading, makeRemoteProcedureCall: syncUserProfiles } = useApplicationStateLoader({
    identifier: ADMIN.SYNC_USER_PROFILES,
    remoteProcedureCall: SYNC_USER_PROFILES_RPC,
    manual: true,
  });
  const [selectedPositionID, setSelectedPositionID] = useState<PositionID | undefined>(undefined);
  const onSelectedPositionIDChange = useCallback(({ target: { value } }: ReactChangeEvent<HTMLSelectElement>) => {
    if (value && value in PositionID) {
      setSelectedPositionID(value as PositionID);
    }
  }, []);
  const [signUpURLCopyLabel, setSignUpURLCopyLabel] = useState<string>(COPY_SIGN_UP_URL_LABELS.COPY);
  const onCopySignUpURL = useCallback(() => {
    if (selectedPositionID) {
      window.navigator.clipboard
        .writeText(
          `${window.location.origin}?position_id=${selectedPositionID}${
            groupAdminIdCode ? `&id_code=${groupAdminIdCode}` : ''
          }`
        )
        .then(() => {
          setSignUpURLCopyLabel(COPY_SIGN_UP_URL_LABELS.COPIED);
          setTimeout(() => {
            setSignUpURLCopyLabel(COPY_SIGN_UP_URL_LABELS.COPY);
          }, 2000);
        });
    }
  }, [selectedPositionID, groupAdminIdCode]);
  const onSyncUserProfiles = useCallback(() => {
    void syncUserProfiles();
  }, [syncUserProfiles]);

  return (
    <div>
      <h3>Tools</h3>
      <div>
        <h5>Create Sign-Up Link:</h5>
        <select value={selectedPositionID || ''} onChange={onSelectedPositionIDChange}>
          <option value="">Select Position</option>
          {Object.keys(PositionID).map((posID) => (
            <option key={posID} value={posID}>
              {PositionNames[posID as PositionID]}
            </option>
          ))}
        </select>
        <button disabled={!selectedPositionID} onClick={onCopySignUpURL}>
          {signUpURLCopyLabel}
        </button>
      </div>
      {adminIsSystemAdmin ? (
        <div>
          <h5>Synchronize User Account Info To Profiles:</h5>
          <button disabled={syncUserProfilesLoading} onClick={onSyncUserProfiles}>
            {syncUserProfilesLoading ? (
              <article
                aria-busy="true"
                style={{ display: 'inline-block', margin: '0 0.5em 0 0', padding: 0 }}
              ></article>
            ) : undefined}
            Sync Now
          </button>
        </div>
      ) : undefined}
    </div>
  );
};
